import type { DocEntityParentId } from "building-types/doc/docEntityConstraintTypes";
import type { DocForeignKeyTable } from "building-types/doc/docForeignKeyTable";
import type { ForeignEntityId } from "../handles/foreignEntityId";

/**
 * Base types for intersecting option sets that don't depend on DocOption.
 */

export const intersectionEffectTypes = ["MODIFY", "GENERATE"] as const;
export type IntersectionEffectType = (typeof intersectionEffectTypes)[number];

/** Fields on DocOption that can be modified by intersection rules. */
export const modifiableFields = ["price", "builderOptionCode", "description", "salesImages"] as const;
export type ModifiableField = (typeof modifiableFields)[number];

export interface ModifySettings {
  readonly optionSetId?: ForeignEntityId<"OPTION_SET"> | null;
  readonly fields?: ModifiableField[];
}

export interface GenerateSettings {
  readonly targetOptionSetId?: ForeignEntityId<"OPTION_SET"> | null;
}

export interface IntersectionEffect {
  readonly type: IntersectionEffectType;
  readonly modifySettings?: ModifySettings;
  readonly generateSettings?: GenerateSettings;
}

export interface IntersectionScope {
  readonly optionSetId: ForeignEntityId<"OPTION_SET">;
  readonly optionIds: ForeignEntityId<"OPTION">[];
  readonly cartesianProductAtSet?: boolean;
}

export interface IntersectionConditions {
  readonly parentId: DocEntityParentId<"OPTION">;
  /**
   * This is a multimap of option set IDs to option IDs, showing which choices this intersecting option is
   * conditioned on. More than one choice per set indicates that `cartesianProductAtSet` is true (for that set).
   */
  readonly choices: DocForeignKeyTable<"OPTION_SET", readonly (ForeignEntityId<"OPTION"> | "true")[]>;
}

interface Formatting {
  readonly id: string;
  readonly sortIndex: number;
}

export interface OptionCodeFormatting extends Formatting {
  readonly optionSetId: ForeignEntityId<"OPTION_SET">;
}

export interface ManualCodeFormatting extends Formatting {
  readonly name: string;
  readonly builderTemplateCode: string;
}

export type CodeFormatting = ManualCodeFormatting | OptionCodeFormatting;

export function isManualCodeFormatting(formatting: CodeFormatting): formatting is ManualCodeFormatting {
  return "builderTemplateCode" in formatting;
}

export function isOptionCodeFormatting(formatting: CodeFormatting): formatting is OptionCodeFormatting {
  return "optionSetId" in formatting;
}
